import React from 'react';
import { Redirect } from 'react-router-dom';
import TextField from '@material-ui/core/TextField';
import Cached from '@material-ui/icons/Cached';
import checkPermissions from '../../shared/checkPermissions';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import Card from 'components/Card/Card.jsx';
import CardBody from 'components/Card/CardBody.jsx';
import CardHeader from 'components/Card/CardHeader.jsx';
import CardIcon from 'components/Card/CardIcon.jsx';
import Button from '../../components/CustomButtons/Button';
import Loader from '../../components/Loader/Loader';
import Notification from '../../components/Notification/Notification';
import { handleError } from '../../shared/utility';
import axios from 'store/axios/axios-gci-exceptions';
import GCIExceptionsTable from '../../containers/GCI/GCIExceptionsTable';
import GCIExceptionsDialog from '../../containers/GCI/GCIExceptionsDialog';
import GCIExceptionFormDialog from '../../containers/GCI/GCIExceptionFormDialog';

class GCIExceptions extends React.Component {
  state = {
    users: [],
    entries: [],
    userFilter: '',
    loading: false,
    selectedUser: null,
    exceptionFormOpen: false,
    editingEntry: null,
    saving: false,
    notification: ''
  };

  componentDidMount() {
    this.loadData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  loadData = async () => {
    this.setState({ loading: true });

    try {
      const responses = await Promise.all([
        axios.get('/users'),
        axios.get('/entries')
      ]);
      const users = Array.isArray(responses[0].data) ? responses[0].data : [];
      const entries = Array.isArray(responses[1].data) ? responses[1].data : [];
      const exceptionCounts = entries.reduce((counts, entry) => {
        const userId = String(entry.user_id);
        counts[userId] = (counts[userId] || 0) + 1;
        return counts;
      }, {});
      const usersWithCounts = users.map(user => ({
        ...user,
        exceptionCount: exceptionCounts[String(user.id)] || 0
      }));

      if (!this.unmounted) {
        this.setState(state => ({
          users: usersWithCounts,
          entries,
          loading: false,
          selectedUser: state.selectedUser
            ? usersWithCounts.find(user => String(user.id) === String(state.selectedUser.id)) || null
            : null
        }));
      }
    } catch (e) {
      handleError(e);
      if (!this.unmounted) {
        this.setState({
          loading: false,
          notification: 'Unable to load GCI exceptions.'
        });
      }
    }
  };

  getSelectedEntries = () => {
    const { selectedUser, entries } = this.state;
    if (!selectedUser) return [];
    return entries.filter(entry => String(entry.user_id) === String(selectedUser.id));
  };

  handleAddException = () => {
    this.setState({ exceptionFormOpen: true, editingEntry: null });
  };

  handleEditException = entry => {
    this.setState({ exceptionFormOpen: true, editingEntry: entry });
  };

  handleSaveException = async exceptionData => {
    const { editingEntry, selectedUser } = this.state;
    if (!selectedUser) return;

    this.setState({ saving: true });
    try {
      if (editingEntry) {
        await axios.put('/entry', {
          id: editingEntry.id,
          user_id: selectedUser.id,
          title: exceptionData.title,
          rule_json: exceptionData.rule_json
        });
      } else {
        await axios.post('/entry', {
          user_id: selectedUser.id,
          title: exceptionData.title,
          rule_json: exceptionData.rule_json
        });
      }

      if (!this.unmounted) {
        this.setState({
          saving: false,
          exceptionFormOpen: false,
          editingEntry: null
        });
        await this.loadData();
      }
    } catch (e) {
      handleError(e);
      if (!this.unmounted) {
        this.setState({
          saving: false,
          notification: 'Unable to save the GCI exception.'
        });
      }
    }
  };

  render() {
    const permitted = checkPermissions('GCI');
    if (!permitted) {
      return <Redirect to="/dashboard" />;
    }

    const {
      users,
      userFilter,
      loading,
      selectedUser,
      exceptionFormOpen,
      editingEntry,
      saving,
      notification
    } = this.state;
    const normalizedUserFilter = userFilter.trim().toLowerCase();
    const filteredUsers = users.filter(user => (
      !normalizedUserFilter
      || String(user.name || '').toLowerCase().indexOf(normalizedUserFilter) !== -1
    ));

    return (
      <div style={{ marginTop: 20 }}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="rose" icon>
                <CardIcon color="success">
                  <strong>Exceptions</strong>
                </CardIcon>
              </CardHeader>
              <CardBody>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={12}>
                    <div style={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 15
                    }}>
                      <h2 style={{ margin: 0 }}>GCI Exceptions</h2>
                      <Button
                        color="primary"
                        disabled={loading}
                        onClick={this.loadData}
                      >
                        <Cached style={{ marginRight: 5 }} />
                        Refresh
                      </Button>
                    </div>

                    {loading && <Loader center={true} />}
                    {!loading && (
                        <div style={{maxWidth:600}}>
                            <TextField
                              fullWidth
                              label="Filter by name"
                              margin="normal"
                              value={userFilter}
                              onChange={e => this.setState({ userFilter: e.target.value })}
                            />
                            <GCIExceptionsTable
                                users={filteredUsers}
                                onSelectUser={user => this.setState({ selectedUser: user })}
                            />
                        </div>
                    )}
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>

        <GCIExceptionsDialog
          open={Boolean(selectedUser)}
          user={selectedUser}
          entries={this.getSelectedEntries()}
          onClose={() => this.setState({ selectedUser: null })}
          onAddException={this.handleAddException}
          onEditException={this.handleEditException}
        />

        <GCIExceptionFormDialog
          open={exceptionFormOpen}
          user={selectedUser}
          entry={editingEntry}
          saving={saving}
          onClose={() => this.setState({ exceptionFormOpen: false, editingEntry: null })}
          onSave={this.handleSaveException}
        />

        {notification && (
          <Notification
            open={true}
            close={() => this.setState({ notification: '' })}
            message={notification}
          />
        )}
      </div>
    );
  }
}

export default GCIExceptions;
